import { ConnectError } from '@bufbuild/connect';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import {
  MODAL_ANIMATION_DURATION,
  VIDEO_POSTER_FRAGMENT,
} from '@/shared/config';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import {
  getFileSrc,
  getImageSrc,
  getRotationCoverScale,
  trackEvent,
} from '@/shared/lib';
import { errorToastCaller, Icon, Result, Spinner } from '@/shared/ui';
import { Portal } from '@/shared/ui/Portal/Portal.tsx';

import { albumModel } from '@/entities/album';
import { assetsModel } from '@/entities/assets';
import { ModalID, modalModel, RemoveModal } from '@/entities/modal';

import type {
  CropBox,
  EditableToolWithControls,
} from '@/features/dentalPhotoToolbar';
import {
  CropOverlay,
  DentalPhotoToolbar,
  EditingBottomBar,
} from '@/features/dentalPhotoToolbar';
import { FOOTER_RESERVE_HEIGHT } from '@/features/dentalPhotoToolbar/config';

import { i18n, SWIPE_CONFIDENCE_THRESHOLD } from '../../config';
import { usePhotoEditing, usePhotoRemoval } from '../../hooks';
import { downloadTransformedPhoto, swipePower } from '../../lib';

import styles from './PhotoSlider.module.scss';

/**
 * Maps a crop box from the image's native coordinate space to the
 * display coordinate space, accounting for CSS rotation and flip.
 *
 * CSS transform chain: `rotate(θ) scaleX(-1)` — scaleX is applied first.
 */
const mapCropBoxToDisplay = (
  box: CropBox,
  rotation: number,
  flipHorizontal: boolean,
): CropBox => {
  const { y, width, height } = box;

  // Step 1: flip (innermost CSS transform, applied first)
  const x = flipHorizontal ? 1 - box.x - width : box.x;

  // Step 2: rotation (nearest 90° snap)
  const nearest90 = Math.round(rotation / 90) * 90;
  const mod = ((nearest90 % 360) + 360) % 360;

  switch (mod) {
    case 90:
      return { x: 1 - y - height, y: x, width: height, height: width };
    case 180:
      return { x: 1 - x - width, y: 1 - y - height, width, height };
    case 270:
      return { x: y, y: 1 - x - width, width: height, height: width };
    default:
      return { x, y, width, height };
  }
};

/**
 * Inverse of `mapCropBoxToDisplay` — converts display-space crop
 * coordinates back to the image's native coordinate space.
 */
const mapCropBoxToImage = (
  box: CropBox,
  rotation: number,
  flipHorizontal: boolean,
): CropBox => {
  const nearest90 = Math.round(rotation / 90) * 90;
  const mod = ((nearest90 % 360) + 360) % 360;

  // Step 1: inverse rotation
  const rotated = ((): CropBox => {
    switch (mod) {
      case 90:
        return {
          x: box.y,
          y: 1 - box.x - box.width,
          width: box.height,
          height: box.width,
        };
      case 180:
        return {
          x: 1 - box.x - box.width,
          y: 1 - box.y - box.height,
          width: box.width,
          height: box.height,
        };
      case 270:
        return {
          x: 1 - box.y - box.height,
          y: box.x,
          width: box.height,
          height: box.width,
        };
      default:
        return box;
    }
  })();

  // Step 2: inverse flip (flip is its own inverse)
  if (flipHorizontal) {
    return { ...rotated, x: 1 - rotated.x - rotated.width };
  }

  return rotated;
};

type PhotoSliderProps = {
  className?: string;
  testID?: string;
};

export const PhotoSlider: FC<PhotoSliderProps> = (props) => {
  const { className, testID } = props;
  const { formatDate, formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();

  const { visible, data } = useAppSelector(
    modalModel.selectors.selectDentalPhotoSliderModal,
  );

  const { activePhotoID, albumID, canEditAlbums } = data;

  const album = useAppSelector((state) =>
    albumModel.selectors.selectById(state, albumID),
  );

  const studyIDs = album?.StudyIDs;
  const photoIDs = album?.StudyAssetIDs;
  const photoIDsLength = photoIDs?.length;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [dragDirection, setDragDirection] = useState(0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [videoError, setVideoError] = useState<
    'unsupported' | 'generic' | null
  >(null);

  const observerRef = useRef<ResizeObserver | null>(null);
  const imageNaturalAspectRef = useRef(1);
  const photoContainerNodeRef = useRef<HTMLDivElement | null>(null);

  const currentPhotoID = photoIDs?.[currentIndex] ?? '';

  const assetMediaKind = useAppSelector(
    assetsModel.selectors.selectAssetMediaKind(currentPhotoID),
  );

  const isVideoAsset = assetMediaKind === 'video';

  const currentVideoFileID = useAppSelector(
    assetsModel.selectors.selectAssetVideoFileID(currentPhotoID),
  );

  const onClose = useCallback(
    () => dispatch(modalModel.actions.closeModal(ModalID.DentalPhotoSlider)),
    [dispatch],
  );

  const editing = usePhotoEditing({
    currentPhotoID,
    photoContainerNodeRef,
    imageNaturalAspectRef,
    containerSize,
    visible,
  });

  const removal = usePhotoRemoval({
    albumID,
    studyIDs,
    photoIDs,
    currentIndex,
    setCurrentIndex,
    onClose,
  });

  const photoContainerRef = useCallback((node: HTMLDivElement | null) => {
    photoContainerNodeRef.current = node;

    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!node) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];

      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(node);
    observerRef.current = observer;
  }, []);

  // Sync initial photo index from modal data
  useEffect(() => {
    if (activePhotoID && photoIDs) {
      const index = photoIDs.findIndex((id) => id === activePhotoID);

      if (index !== -1) {
        setCurrentIndex(index);
      }
    }
  }, [activePhotoID, photoIDs]);

  useEffect(() => {
    setIsImageLoading(true);
  }, [currentIndex]);

  useEffect(() => {
    setVideoError(null);
  }, [currentVideoFileID]);

  // Navigation
  const handleNext = useCallback(() => {
    if (!photoIDsLength) {
      return;
    }

    setCurrentIndex((prev) => (prev === photoIDsLength - 1 ? prev : prev + 1));
  }, [photoIDsLength]);

  const handlePrev = useCallback(() => {
    if (!photoIDsLength) {
      return;
    }

    setCurrentIndex((prev) => (prev === 0 ? prev : prev - 1));
  }, [photoIDsLength]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!visible) {
        return;
      }

      if (editing.isEditing) {
        if (event.key === 'Escape') {
          editing.handleCancel();
        }

        return;
      }

      if (event.key === 'ArrowRight') {
        handleNext();
      }

      if (event.key === 'ArrowLeft') {
        handlePrev();
      }

      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visible,
    editing.isEditing,
    editing.handleCancel,
    handleNext,
    handlePrev,
    onClose,
  ]);

  const handleVideoError = (event: SyntheticEvent<HTMLVideoElement>) => {
    const code = event.currentTarget.error?.code;

    if (code === MediaError.MEDIA_ERR_ABORTED) {
      return;
    }

    setVideoError(
      code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
        ? 'unsupported'
        : 'generic',
    );
  };

  const handleDownload = async () => {
    if (!photoIDs) {
      return;
    }

    const activePhoto = photoIDs[currentIndex];

    trackEvent({
      eventName: 'DentalPhotosOpenedPhotoDownloadClicked',
      properties: {
        photo_id: activePhoto,
        album_id: albumID,
      },
    });

    if (isVideoAsset && currentVideoFileID) {
      const link = document.createElement('a');
      link.href = getFileSrc(currentVideoFileID);
      link.download = `video_${currentVideoFileID}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }

    try {
      await downloadTransformedPhoto(activePhoto, `photo_${activePhoto}`, {
        rotation: editing.totalRotation,
        flipHorizontal: editing.transformState.flipHorizontal,
        cropBox: editing.transformState.cropBox,
        zoom: editing.transformState.zoom,
        panX: editing.transformState.panX,
        panY: editing.transformState.panY,
      });
    } catch (error) {
      errorToastCaller(error as ConnectError);
    }
  };

  // Computed transform styles
  const isCropMode = editing.activeTool === 'crop';
  const isStraightenMode = editing.activeTool === 'straighten';
  const isCropApplied = editing.transformState.cropBox !== null && !isCropMode;

  const compensatingZoom = getRotationCoverScale(
    editing.totalRotation,
    imageNaturalAspectRef.current,
  );

  // At 90°/270° the rotated photo needs extra scaling to contain-fit
  // the fixed-size container (which no longer shrinks to the image).
  const rotationContainScale = (() => {
    const nearest90 = Math.round(editing.totalRotation / 90) * 90;
    const nearestMod = ((nearest90 % 360) + 360) % 360;

    if (nearestMod !== 90 && nearestMod !== 270) {
      return 1;
    }

    const cw = containerSize.width || 1;
    const ch = containerSize.height || 1;
    const a = imageNaturalAspectRef.current;
    const ca = cw / ch;

    // Unrotated contain-fit dimensions of the image
    const imgW = a > ca ? cw : ch * a;
    const imgH = a > ca ? cw / a : ch;

    // Scale so the rotated (imgH × imgW) contain-fits in (cw × ch)
    return Math.min(cw / imgH, ch / imgW);
  })();

  const totalZoom =
    editing.transformState.zoom *
    Math.max(compensatingZoom, rotationContainScale);

  // In crop mode, keep rotation/flip/contain-scale so the photo stays in its
  // rotated orientation. CropOverlay coordinates are mapped between display
  // and image space via mapCropBoxToDisplay/mapCropBoxToImage.
  const cropModeScale = Math.max(compensatingZoom, rotationContainScale);

  const photoTransformStyle: React.CSSProperties = isCropMode
    ? {
        transform: `scale(${cropModeScale}) rotate(${editing.totalRotation}deg)${editing.transformState.flipHorizontal ? ' scaleX(-1)' : ''}`,
      }
    : {
        transform: `translate(${editing.transformState.panX * 100}%, ${editing.transformState.panY * 100}%) scale(${totalZoom}) rotate(${editing.totalRotation}deg) scaleX(${editing.transformState.flipHorizontal ? -1 : 1})`,
      };

  const computeCropWrapperStyle = (): React.CSSProperties => {
    const { width, height } = editing.transformState.cropBox!;
    const imgAspect = imageNaturalAspectRef.current;
    const cropAspect = (width / height) * imgAspect;

    const maxW = containerSize.width || window.innerWidth - 200;
    const maxH =
      containerSize.height || window.innerHeight - FOOTER_RESERVE_HEIGHT;

    if (
      width <= 0 ||
      height <= 0 ||
      imgAspect <= 0 ||
      cropAspect <= 0 ||
      maxW <= 0 ||
      maxH <= 0
    ) {
      return {
        position: 'relative',
        overflow: 'hidden',
        width: Math.max(maxW, 1),
        height: Math.max(maxH, 1),
      };
    }

    const fitByWidthH = maxW / cropAspect;

    const displayW = fitByWidthH <= maxH ? maxW : maxH * cropAspect;
    const displayH = fitByWidthH <= maxH ? fitByWidthH : maxH;

    return {
      position: 'relative',
      overflow: 'hidden',
      width: displayW,
      height: displayH,
    };
  };

  const computeImgCropStyle = (): React.CSSProperties => {
    const { x, y, width, height } = editing.transformState.cropBox!;
    const imgAspect = imageNaturalAspectRef.current;
    const cropAspect = (width / height) * imgAspect;

    if (width <= 0 || height <= 0 || imgAspect <= 0 || cropAspect <= 0) {
      return {};
    }

    const cropCenterX = x + width / 2;
    const cropCenterY = y + height / 2;
    const imageWidth = 1 / width;
    const imageHeight = 1 / height;

    return {
      position: 'absolute',
      width: `${imageWidth * 100}%`,
      height: `${imageHeight * 100}%`,
      left: `${50 - cropCenterX * imageWidth * 100}%`,
      top: `${50 - cropCenterY * imageHeight * 100}%`,
      maxWidth: 'none',
      maxHeight: 'none',
    };
  };

  const cropWrapperStyle = isCropApplied
    ? computeCropWrapperStyle()
    : undefined;
  const imgCropStyle = isCropApplied ? computeImgCropStyle() : undefined;

  // Effective aspect ratio accounting for 90°/270° base rotations
  const effectiveAspect = (() => {
    const nearest90 = Math.round(editing.totalRotation / 90) * 90;
    const nearestMod = ((nearest90 % 360) + 360) % 360;

    return nearestMod === 90 || nearestMod === 270
      ? 1 / imageNaturalAspectRef.current
      : imageNaturalAspectRef.current;
  })();

  // Insets describing the gap between container edges and the contain-fitted photo
  const photoInsets = (() => {
    const cw = containerSize.width || 1;
    const ch = containerSize.height || 1;
    const aspect = effectiveAspect;
    const containerAspect = cw / ch;

    if (aspect > containerAspect) {
      const imgH = cw / aspect;
      const top = (ch - imgH) / 2;

      return { top, bottom: top, left: 0, right: 0 };
    }

    const imgW = ch * aspect;
    const left = (cw - imgW) / 2;

    return { top: 0, bottom: 0, left, right: left };
  })();

  const gridOverlayStyle: React.CSSProperties = photoInsets;

  // Clip the container to the photo's display area so dark margins
  // don't form visible triangles at the corners during rotation.
  // Skip during crop mode — CropOverlay uses full container dimensions.
  const photoClipStyle: React.CSSProperties | undefined =
    compensatingZoom > 1 && !isCropMode
      ? {
          clipPath: `inset(${photoInsets.top}px ${photoInsets.right}px ${photoInsets.bottom}px ${photoInsets.left}px round 8px)`,
        }
      : undefined;

  return (
    <AnimatePresence key="slider-animate-presence">
      {visible && (
        <Portal>
          <div
            className={cn(styles.container, className)}
            data-testid={testID}
            data-component="photo-slider"
          >
            <motion.div
              className={styles.overlay}
              onClick={editing.isEditing ? undefined : onClose}
              key="slider-overlay"
              transition={{ duration: MODAL_ANIMATION_DURATION }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <motion.div
              className={cn(styles.container)}
              key="slider"
              exit="exit"
              variants={{
                initial: { scale: 0, opacity: 0 },
                exit: { scale: 0, opacity: 0 },
                open: {
                  scale: 1,
                  opacity: 1,
                  transition: { duration: MODAL_ANIMATION_DURATION },
                },
                closed: {
                  scale: 0,
                  opacity: 0,
                  transition: { duration: MODAL_ANIMATION_DURATION },
                },
              }}
              initial="initial"
              animate={visible ? 'open' : 'closed'}
            >
              <header className={styles.header}>
                <button className={styles.close} onClick={onClose}>
                  <Icon name="close" size={40} />
                </button>
              </header>

              <div className={styles.bodyWrapper}>
                <DentalPhotoToolbar
                  className={styles.toolbar}
                  activeTool={editing.activeTool}
                  isMirrored={editing.transformState.flipHorizontal}
                  canEdit={canEditAlbums}
                  mode={isVideoAsset ? 'video' : 'photo'}
                  onToolSelect={editing.handleToolSelect}
                  onDownload={handleDownload}
                  onDelete={removal.handleOpenRemoveModal}
                />

                <div className={styles.body}>
                  {!isVideoAsset && isImageLoading && (
                    <Spinner className={styles.spinner} size={48} primary />
                  )}

                  {photoIDs?.[currentIndex] && (
                    <motion.div
                      key={`photo-wrapper-${photoIDs[currentIndex]}`}
                      className={styles.photoWrapper}
                      drag={isMobile && !isVideoAsset ? 'x' : false}
                      dragListener={!editing.isEditing && !isVideoAsset}
                      dragConstraints={{ left: 0, right: 0 }}
                      dragElastic={0.2}
                      onDragEnd={(_, { offset, velocity }) => {
                        const swipe = swipePower(offset.x, velocity.x);

                        if (swipe < -SWIPE_CONFIDENCE_THRESHOLD) {
                          setDragDirection(1);
                          handleNext();
                        } else if (swipe > SWIPE_CONFIDENCE_THRESHOLD) {
                          setDragDirection(-1);
                          handlePrev();
                        }
                      }}
                      initial={{ opacity: 0, x: dragDirection * 300 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: dragDirection * -300 }}
                      transition={{ duration: 0.3 }}
                    >
                      {assetMediaKind === 'video' ? (
                        currentVideoFileID &&
                        (videoError ? (
                          <Result
                            className={styles.videoFallback}
                            textClassName={styles.videoFallbackText}
                            icon={<Icon name="alert" size={48} />}
                            text={
                              videoError === 'unsupported'
                                ? formatMessage(
                                    i18n.photoSliderMessages
                                      .videoUnsupportedFormat,
                                  )
                                : formatMessage(
                                    i18n.photoSliderMessages.videoPlaybackError,
                                  )
                            }
                          />
                        ) : (
                          <video
                            key={`video-${currentVideoFileID}`}
                            className={styles.video}
                            src={`${getFileSrc(currentVideoFileID)}${VIDEO_POSTER_FRAGMENT}`}
                            controls
                            autoPlay
                            muted
                            preload="metadata"
                            playsInline
                            onError={handleVideoError}
                          />
                        ))
                      ) : assetMediaKind === 'photo' ? (
                        <div
                          ref={photoContainerRef}
                          className={cn(
                            styles.photoContainer,
                            (compensatingZoom > 1 ||
                              editing.isZoomedIn ||
                              editing.isDirty) &&
                              styles.clipped,
                          )}
                          style={photoClipStyle}
                        >
                          <div className={styles.photoTransform}>
                            <div
                              className={cn(
                                !isCropApplied
                                  ? styles.photoInnerWrapper
                                  : undefined,
                                editing.isZoomedIn && styles.pannable,
                                isStraightenMode && styles.noTransition,
                              )}
                              style={{
                                ...cropWrapperStyle,
                                ...photoTransformStyle,
                              }}
                              onPointerDown={editing.handlePanPointerDown}
                              onPointerMove={editing.handlePanPointerMove}
                              onPointerUp={editing.handlePanPointerUp}
                            >
                              <img
                                className={cn(
                                  styles.photo,
                                  isImageLoading && styles.photoHidden,
                                )}
                                style={imgCropStyle}
                                src={getImageSrc(
                                  photoIDs[currentIndex],
                                  'optimized',
                                )}
                                alt={`${formatMessage({
                                  id: 'imgAltText.photo',
                                  defaultMessage: 'Photo',
                                })} ${currentIndex + 1}`}
                                draggable={false}
                                onLoad={(e) => {
                                  const img = e.currentTarget;

                                  if (img.naturalWidth && img.naturalHeight) {
                                    imageNaturalAspectRef.current =
                                      img.naturalWidth / img.naturalHeight;
                                  }

                                  setIsImageLoading(false);
                                }}
                              />
                            </div>
                          </div>

                          {isStraightenMode && (
                            <div
                              className={styles.gridOverlay}
                              style={gridOverlayStyle}
                            />
                          )}

                          {isCropMode && editing.transformState.cropBox && (
                            <div
                              className={styles.cropImageBounds}
                              style={gridOverlayStyle}
                            >
                              <CropOverlay
                                className={styles.cropOverlay}
                                containerWidth={
                                  containerSize.width -
                                  photoInsets.left -
                                  photoInsets.right
                                }
                                containerHeight={
                                  containerSize.height -
                                  photoInsets.top -
                                  photoInsets.bottom
                                }
                                cropBox={mapCropBoxToDisplay(
                                  editing.transformState.cropBox,
                                  editing.totalRotation,
                                  editing.transformState.flipHorizontal,
                                )}
                                onChange={(displayBox) =>
                                  editing.setCropBox(
                                    mapCropBoxToImage(
                                      displayBox,
                                      editing.totalRotation,
                                      editing.transformState.flipHorizontal,
                                    ),
                                  )
                                }
                              />
                            </div>
                          )}
                        </div>
                      ) : null}
                    </motion.div>
                  )}
                </div>
              </div>

              <footer className={styles.footer}>
                {editing.isEditing ? (
                  <EditingBottomBar
                    className={styles.editingBar}
                    activeTool={editing.activeTool as EditableToolWithControls}
                    rotation={editing.transformState.freeRotation}
                    onDone={editing.handleDone}
                    onCancel={editing.handleCancel}
                    onResetToOriginal={editing.handleResetToOriginal}
                    onRotateLeft={editing.rotate90CCW}
                    onRotateRight={editing.rotate90CW}
                    onZoomIn={editing.zoomIn}
                    onZoomOut={editing.zoomOut}
                    onFreeRotation={editing.setFreeRotation}
                  />
                ) : (
                  <>
                    {!isMobile && (
                      <div className={cn(styles.fileName, 'p1')}>
                        {formatDate(album?.Time?.toDate())} {album?.Name}
                      </div>
                    )}
                  </>
                )}
              </footer>

              {!isMobile && !editing.isEditing && (photoIDsLength ?? 0) > 1 && (
                <>
                  <div
                    className={cn(
                      styles.arrowLeft,
                      currentIndex === 0 && styles.disabled,
                    )}
                    onClick={handlePrev}
                  >
                    <Icon name="arrowDown" />
                  </div>
                  <div
                    className={cn(
                      styles.arrowRight,
                      currentIndex === (photoIDsLength ?? 0) - 1 &&
                        styles.disabled,
                    )}
                    onClick={handleNext}
                  >
                    <Icon name="arrowDown" />
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </Portal>
      )}

      <RemoveModal
        key="remove-modal"
        isOpen={removal.isRemoveModalOpen}
        message={formatMessage({
          id: 'dentalPhoto.removePhotoModal.message',
          defaultMessage: 'This action is irreversible',
        })}
        title={formatMessage({
          id: 'dentalPhoto.removePhotoModal.title',
          defaultMessage: 'Remove this photo',
        })}
        onCancel={removal.handleCloseRemoveModal}
        onRemoveCallback={removal.handleRemove}
      />
    </AnimatePresence>
  );
};
